import React, { useState, useEffect, useMemo } from "react";
import { Card, CardBody, Col, Button, Spinner, Row, Input, FormGroup, Label, FormFeedback, Table } from "reactstrap";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import axios from "axios";
import { baseUrl } from "../../Utils/FileUpload";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import { FaSave } from "react-icons/fa";

interface PermissionObj {
  id: number;
  slug: string;
  action: string;
}

export default function EditRole() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const location = useLocation();
  const roleId = location.state?.id;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [errors, setErrors] = useState<{ name?: string; description?: string }>({});
  const [permissionError, setPermissionError] = useState(false);

  const [permissionsData, setPermissionsData] = useState<Record<string, Record<string, PermissionObj[]>>>({});
  const [actionHeaders, setActionHeaders] = useState<string[]>([]);
  const [formattedHeaders, setFormattedHeaders] = useState<string[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);

  useEffect(() => {
    if (!roleId) {
      toast.error("No role selected for editing.");
      navigate("/role");
      return;
    }

    const loadRoleData = async () => {
      setLoading(true);
      try {
        const [permRes, roleRes] = await Promise.all([
          axios.post(`${baseUrl}role/get_permissions`, {}, { headers: { Authorization: `Bearer ${token}` } }),
          axios.post(`${baseUrl}role/viewbyid`, { id: roleId }, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        setPermissionsData(permRes.data.permissions || {});
        setActionHeaders(permRes.data.action_headers || []);
        setFormattedHeaders(permRes.data.formatted_action_headers || []);

        const role = roleRes.data;
        setName(role.name || "");
        setDescription(role.description || "");
        if (role.permissions) {
          setSelectedPermissions(role.permissions.map((p: any) => p.id));
        }
      } catch (error) {
        console.error("Error loading edit role data:", error);
        toast.error("Failed to load role details.");
      } finally {
        setLoading(false);
      }
    };

    loadRoleData();
  }, [roleId, token, navigate]);

  // Combine all modules into a single flat map, excluding SMS Setting & Payment Setting
  const EXCLUDED_MODULES = ["SMS Setting", "Payment Setting"];

  const allModules = useMemo(() => {
    const merged: Record<string, PermissionObj[]> = {};
    Object.values(permissionsData).forEach((modules) => {
      Object.entries(modules).forEach(([moduleName, perms]) => {
        if (!EXCLUDED_MODULES.includes(moduleName)) {
          merged[moduleName] = perms;
        }
      });
    });
    return merged;
  }, [permissionsData]);

  // Smart Toggle: 'Add' works standalone without 'View'.
  // ALL OTHER actions (Edit, Delete, Enable/Disable, Payout, Reports, etc.) require 'View'.
  const handleTogglePermission = (targetPerm: PermissionObj, modulePerms: PermissionObj[]) => {
    const targetId = targetPerm.id;
    const targetAction = targetPerm.action;
    const viewPerm = modulePerms.find((p) => p.action === "view");

    setSelectedPermissions((prev) => {
      const isCurrentlyChecked = prev.includes(targetId);
      let next: number[];

      if (isCurrentlyChecked) {
        // Unchecking
        next = prev.filter((id) => id !== targetId);
        // If unchecking 'view', automatically uncheck all actions EXCEPT 'add'
        if (targetAction === "view") {
          const nonAddModuleIds = modulePerms.filter((p) => p.action !== "add").map((p) => p.id);
          next = next.filter((id) => !nonAddModuleIds.includes(id));
        }
      } else {
        // Checking
        next = [...prev, targetId];
        // If checking any action OTHER THAN 'add' or 'view', automatically check 'view' for this module
        if (targetAction !== "add" && targetAction !== "view" && viewPerm && !next.includes(viewPerm.id)) {
          next.push(viewPerm.id);
        }
      }

      if (next.length > 0) setPermissionError(false);
      return next;
    });
  };

  const isModuleAllSelected = (modulePermissions: PermissionObj[]) => {
    if (!modulePermissions || modulePermissions.length === 0) return false;
    return modulePermissions.every((p) => selectedPermissions.includes(p.id));
  };

  const handleToggleModuleAll = (modulePermissions: PermissionObj[]) => {
    const moduleIds = modulePermissions.map((p) => p.id);
    const allSelected = isModuleAllSelected(modulePermissions);

    if (allSelected) {
      setSelectedPermissions((prev) => prev.filter((id) => !moduleIds.includes(id)));
    } else {
      setSelectedPermissions((prev) => {
        const next = Array.from(new Set([...prev, ...moduleIds]));
        if (next.length > 0) setPermissionError(false);
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let hasError = false;
    const newErrors: { name?: string; description?: string } = {};

    if (!name.trim()) {
      newErrors.name = "Role name is required.";
      hasError = true;
    }

    if (!description.trim()) {
      newErrors.description = "Description is required.";
      hasError = true;
    }

    if (selectedPermissions.length === 0) {
      setPermissionError(true);
      hasError = true;
      document.getElementById("permissions-matrix-table")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    // Validation: All actions EXCEPT 'add' require 'view'
    let missingViewInfo: { moduleName: string; actionName: string } | null = null;
    Object.entries(allModules).forEach(([moduleName, perms]) => {
      const selectedForModule = perms.filter((p) => selectedPermissions.includes(p.id));
      const hasView = selectedForModule.some((p) => p.action === "view");
      const nonAddSelected = selectedForModule.find((p) => p.action !== "view" && p.action !== "add");

      if (nonAddSelected && !hasView) {
        const formattedAction =
          nonAddSelected.action === "enable_disable"
            ? "Enable / Disable"
            : nonAddSelected.action === "download_pdf"
              ? "Download PDF"
              : nonAddSelected.action === "download_excel"
                ? "Download Excel"
                : ucwords(nonAddSelected.action);

        missingViewInfo = { moduleName, actionName: formattedAction };
      }
    });

    if (missingViewInfo) {
      const info = missingViewInfo as { moduleName: string; actionName: string };
      toast.error(
        `For module "${info.moduleName}", selecting "${info.actionName}" requires View permission.`
      );
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${baseUrl}role/update`,
        {
          id: roleId,
          name: name.trim(),
          description: description.trim(),
          permissions: selectedPermissions,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Role updated successfully!");
      setTimeout(() => {
        navigate("/role");
      }, 1000);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to update role.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-body d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="page-body">
      <Breadcrumbs pageTitle="Edit Role" parent="Role Management" title="Edit Role" />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody className="p-4">
            <form onSubmit={handleSubmit} autoComplete="off">
              <Row className="mb-2">
                <Col xl={6}>
                  <FormGroup>
                    <Label className="font-weight-bold">
                      Role Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      placeholder="Enter role name"
                      value={name}
                      autoComplete="off"
                      onChange={(e) => {
                        setName(e.target.value);
                        setErrors((prev) => ({ ...prev, name: undefined }));
                      }}
                      invalid={!!errors.name}
                    />
                    <FormFeedback>{errors.name}</FormFeedback>
                  </FormGroup>
                </Col>
                <Col xl={6}>
                  <FormGroup>
                    <Label className="font-weight-bold">
                      Description <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      placeholder="Enter description of role responsibilities"
                      value={description}
                      autoComplete="off"
                      onChange={(e) => {
                        setDescription(e.target.value);
                        setErrors((prev) => ({ ...prev, description: undefined }));
                      }}
                      invalid={!!errors.description}
                    />
                    <FormFeedback>{errors.description}</FormFeedback>
                  </FormGroup>
                </Col>
              </Row>

              <div className="d-flex justify-content-between align-items-center mb-3 ">
                <h5 className="font-weight-bold">
                  Permissions Matrix <span className="text-danger">*</span>
                </h5>
              </div>

              <div
                id="permissions-matrix-table"
                className="table-responsive custom-scrollbar mb-4"
                style={
                  permissionError
                    ? {
                      border: "1px solid #dc3545",
                      borderRadius: "6px",
                      boxShadow: "0 0 0 3px rgba(220,53,69,0.15)",
                      transition: "box-shadow 0.2s ease",
                    }
                    : { border: "1px solid transparent", borderRadius: "6px", transition: "box-shadow 0.2s ease" }
                }
              >
                <Table bordered hover striped size="sm" className="mb-0 align-middle text-center" style={{ fontSize: "12px", tableLayout: "auto" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f8f9fa" }}>
                      <th className="text-start ps-3" style={{ minWidth: "150px", color: "#308e87", padding: "10px 8px", fontSize: "13px" }}>
                        Module
                      </th>
                      {formattedHeaders.map((header) => (
                        <th
                          key={header}
                          className="text-center align-middle"
                          style={{
                            minWidth: "68px",
                            color: "#308e87",
                            padding: "10px 2px",
                            fontSize: "11px",
                            lineHeight: "1.2",
                            fontWeight: "600",
                          }}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(allModules).map(([moduleName, modulePerms]) => {
                      const allChecked = isModuleAllSelected(modulePerms);
                      return (
                        <tr key={moduleName}>
                          <td className="text-start ps-3" style={{ padding: "10px 8px" }}>
                            <div className="d-flex align-items-center gap-1">
                              <Input
                                type="checkbox"
                                id={`module-${moduleName}`}
                                checked={allChecked}
                                onChange={() => handleToggleModuleAll(modulePerms)}
                                style={{ cursor: "pointer", margin: 0 }}
                              />
                              <Label
                                for={`module-${moduleName}`}
                                className="mb-0 font-weight-bold text-nowrap"
                                style={{ cursor: "pointer", fontSize: "12px" }}
                              >
                                {moduleName}
                              </Label>
                            </div>
                          </td>
                          {actionHeaders.map((actionKey) => {
                            const permObj = modulePerms.find((p) => p.action === actionKey);
                            if (!permObj) {
                              return (
                                <td key={actionKey} className="text-center text-muted" style={{ padding: "10px 2px" }}>
                                  —
                                </td>
                              );
                            }
                            const isChecked = selectedPermissions.includes(permObj.id);
                            return (
                              <td key={actionKey} className="text-center" style={{ padding: "10px 2px" }}>
                                <Input
                                  type="checkbox"
                                  id={`perm-${permObj.id}`}
                                  checked={isChecked}
                                  onChange={() => handleTogglePermission(permObj, modulePerms)}
                                  style={{ cursor: "pointer", margin: 0 }}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {permissionError && (
                <div className="text-danger mt-2 font-weight-bold" style={{ fontSize: "13px" }}>
                  Please select at least one permission for this role.
                </div>
              )}

              <div className="d-flex gap-2 justify-content-start mt-4 pt-2">
                <Button color="primary" type="submit" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Spinner size="sm" className="me-1" /> Updating...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-1" /> Update Role
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      </Col>
      <ToastContainer />
    </div>
  );
}

function ucwords(str: string) {
  return str.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}
